<template>
  <div class="lp-page integrations-page">
    <Navbar />

    <!-- Hero -->
    <section class="lp-hero">
      <div class="lp-container">
        <span class="lp-eyebrow">{{ $t('integrations.heroLabel') }}</span>
        <h1 class="lp-title">{{ $t('integrations.heroTitle') }}</h1>
        <p class="lp-lead">{{ $t('integrations.heroSubtitle') }}</p>
      </div>
    </section>

    <!-- Formats grid -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-section-head">
          <h2 class="lp-h2">{{ $t('integrations.sectionTitle') }}</h2>
          <p class="lp-lead">{{ $t('integrations.sectionSubtitle') }}</p>
        </div>
        <div class="lp-grid lp-grid-3">
          <div class="lp-card fmt-card" v-for="(f, i) in formats" :key="i">
            <div class="fmt-head">
              <span class="lp-ico" :class="f.color"><font-awesome-icon :icon="f.icon" /></span>
              <div>
                <h3 class="fmt-title">{{ f.title }}</h3>
                <span class="fmt-badge">{{ f.badgeKey ? $t(f.badgeKey) : f.badge }}</span>
              </div>
            </div>
            <p class="lp-text">{{ $t(f.desc) }}</p>
            <ul class="fmt-features">
              <li v-for="(feat, k) in f.features" :key="k">
                <font-awesome-icon icon="fa-solid fa-circle-check" />
                <span>{{ $t(feat) }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Custom export -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <div class="custom-card reveal-item">
          <span class="lp-ico violet"><font-awesome-icon icon="fa-solid fa-code" /></span>
          <div class="custom-text">
            <h2 class="lp-h3">{{ $t('integrations.customExportTitle') }}</h2>
            <p class="lp-text">{{ $t('integrations.customExportMessage') }}</p>
          </div>
          <a href="/contact" class="lp-btn lp-btn-primary custom-btn">
            <font-awesome-icon icon="fa-solid fa-paper-plane" /> {{ $t('integrations.contactUs') }}
          </a>
        </div>
      </div>
    </section>

    <!-- Learn more -->
    <section class="lp-section-sm">
      <div class="lp-container">
        <div class="learn-card reveal-item">
          <span class="lp-ico amber"><font-awesome-icon icon="fa-solid fa-newspaper" /></span>
          <div class="learn-text">
            <h3 class="lp-h3">{{ $t('integrations.learnMoreTitle') }}</h3>
            <p class="lp-text">{{ $t('integrations.learnMoreDescription') }}</p>
          </div>
          <a href="/blog" class="learn-link">{{ $t('integrations.visitBlog') }} <font-awesome-icon icon="fa-solid fa-arrow-right" /></a>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="lp-section">
      <div class="lp-container">
        <div class="lp-cta">
          <h2>{{ $t('integrations.cta.title') }}</h2>
          <p>{{ $t('integrations.cta.subtitle') }}</p>
          <div class="cta-buttons">
            <a href="/auth/google" class="lp-btn lp-btn-primary">{{ $t('integrations.cta.startTrial') }}</a>
            <a href="/pricing" class="lp-btn lp-btn-secondary cta-secondary">{{ $t('integrations.cta.viewPricing') }}</a>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import Navbar from '@/components/Navbar.vue';
import Footer from '@/components/Footer.vue';
import { useReveal } from '../composables/useReveal';

const formats = [
  { title: 'PDF', icon: 'fa-solid fa-file-pdf', color: 'pink', badge: 'PDF', desc: 'integrations.pdfDescription', features: ['integrations.individualFiles', 'integrations.zipArchive', 'integrations.originalFormat'] },
  { title: 'TXT - NAP', icon: 'fa-solid fa-file-lines', color: 'blue', badgeKey: 'integrations.purchases', desc: 'integrations.napPurchasesDescription', features: ['integrations.purchaseInvoices', 'integrations.napCompliant', 'integrations.readyToImport'] },
  { title: 'TXT - NAP', icon: 'fa-solid fa-file-lines', color: 'violet', badgeKey: 'integrations.sales', desc: 'integrations.napSalesDescription', features: ['integrations.salesInvoices', 'integrations.napCompliant', 'integrations.readyToImport'] },
  { title: 'MicroInvest XML', icon: 'fa-solid fa-file-code', color: 'amber', badge: 'XML', desc: 'integrations.microInvestDescription', features: ['integrations.deltaProCompatible', 'integrations.structuredData', 'integrations.widelyUsed'] },
  { title: 'Konto XLSX', icon: 'fa-solid fa-file-excel', color: 'green', badge: 'XLSX', desc: 'integrations.kontoDescription', features: ['integrations.kontoFormat', 'integrations.excelCompatible', 'integrations.easyIntegration'] },
  { title: 'Excel', icon: 'fa-solid fa-file-excel', color: 'green', badge: 'XLSX', desc: 'integrations.excelDescription', features: ['integrations.allInvoiceData', 'integrations.spreadsheetFormat', 'integrations.easyAnalysis'] },
  { title: 'Excel Detailed', icon: 'fa-solid fa-table', color: 'cyan', badge: 'XLSX', desc: 'integrations.excelDetailedDescription', features: ['integrations.detailedBreakdown', 'integrations.lineItems', 'integrations.fullAnalysis'] },
  { title: 'PlusMinus CSV', icon: 'fa-solid fa-file-csv', color: 'blue', badge: 'CSV', desc: 'integrations.plusMinusDescription', features: ['integrations.universalFormat7', 'integrations.bulgarianAccounting', 'integrations.csvFormat'] },
  { title: 'HTML', icon: 'fa-solid fa-code', color: 'violet', badge: 'HTML', desc: 'integrations.htmlDescription', features: ['integrations.webReady', 'integrations.printable', 'integrations.professionalLayout'] },
];

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' });
});

useReveal();
</script>

<style scoped>
.fmt-card { display: flex; flex-direction: column; }
.fmt-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
.fmt-head .lp-ico { margin-bottom: 0; width: 50px; height: 50px; font-size: 20px; flex-shrink: 0; }
.fmt-title { font-size: 17px; font-weight: 700; color: var(--ink); margin: 0 0 4px; }
.fmt-badge { display: inline-block; padding: 3px 10px; border-radius: var(--pill); background: rgba(43,130,240,0.1); color: var(--violet); font-size: 11px; font-weight: 700; letter-spacing: 0.02em; }
.fmt-card .lp-text { flex: 1; margin-bottom: 16px; font-size: 14.5px; }
.fmt-features { list-style: none; margin: 0; padding: 16px 0 0; border-top: 1px solid var(--line); display: flex; flex-direction: column; gap: 10px; }
.fmt-features li { display: flex; align-items: flex-start; gap: 9px; font-size: 13.5px; color: var(--ink); }
.fmt-features li svg { color: var(--violet); margin-top: 2px; flex-shrink: 0; }

.custom-card, .learn-card { display: flex; align-items: center; gap: 22px; background: var(--card); border: 1px solid var(--line); border-radius: var(--radius); padding: 28px 32px; box-shadow: 0 22px 54px -40px rgba(31,41,51,0.3); }
.custom-card .lp-ico, .learn-card .lp-ico { margin-bottom: 0; flex-shrink: 0; }
.custom-text, .learn-text { flex: 1; }
.custom-text .lp-h3, .learn-text .lp-h3 { margin-bottom: 6px; }
.custom-btn { flex-shrink: 0; }
.learn-link { display: inline-flex; align-items: center; gap: 8px; color: var(--violet); font-weight: 600; font-size: 14.5px; flex-shrink: 0; transition: gap 0.2s ease; }
.learn-card:hover .learn-link { gap: 12px; }

.cta-buttons { display: inline-flex; gap: 14px; flex-wrap: wrap; justify-content: center; }
.cta-secondary { background: rgba(255,255,255,0.1); color: #fff; border-color: rgba(255,255,255,0.25); }
.cta-secondary:hover { background: rgba(255,255,255,0.18); color: #fff; border-color: rgba(255,255,255,0.4); }

@media (max-width: 768px) {
  .custom-card, .learn-card { flex-direction: column; align-items: flex-start; gap: 16px; }
}
</style>
